import React, { useState, useEffect } from "react";
import "../../../styles/index.less";
import service from "../../../services/service";
import { useParameters } from "../../../context/parametersContext";
import {
  findLatestValidValue,
  formatNumber,
  getLatestValidQuarter,
  toWholeNumber,
} from "../../../utils/financialHelper";
import { useAuth } from "../../../context/authContext";
import { toast } from "react-toastify";
import { NavLink, useNavigate } from "react-router-dom";
import LoadingOverlay from "../../../shared/LoadingOverlay/LoadingOverlay";

const invitLogos: Record<string, string> = {
  "IRB InvIT Fund": new URL(
    "../../../assets/invit-logos/irb.png",
    import.meta.url
  ).href,
  "Indus Infra Trust": new URL(
    "../../../assets/invit-logos/indus.jpg",
    import.meta.url
  ).href,
  "PowerGrid InvIT": new URL(
    "../../../assets/invit-logos/powergrid.jpg",
    import.meta.url
  ).href,
  "IndiGrid InvIT": new URL(
    "../../../assets/invit-logos/indigrid.jpg",
    import.meta.url
  ).href,
  "Capital Infra Trust": new URL(
    "../../../assets/invit-logos/capitalinfra.jpg",
    import.meta.url
  ).href,
  "Altius Telecom Infrastructure Trust": new URL(
    "../../../assets/invit-logos/altius.jpg",
    import.meta.url
  ).href,
  "Anzen India Energy Yield Plus Trust": new URL(
    "../../../assets/invit-logos/anzen.png",
    import.meta.url
  ).href,
  "Cube Highways Trust": new URL(
    "../../../assets/invit-logos/cube.png",
    import.meta.url
  ).href,
  "Energy Infrastructure Trust": new URL(
    "../../../assets/invit-logos/energy.png",
    import.meta.url
  ).href,
  "Vertis Infrastructure Trust": new URL(
    "../../../assets/invit-logos/vertis.jpg",
    import.meta.url
  ).href,
  "Interise Trust": new URL(
    "../../../assets/invit-logos/interise.png",
    import.meta.url
  ).href,
  "Maple Infrastructure Trust": new URL(
    "../../../assets/invit-logos/maple.png",
    import.meta.url
  ).href,
  "NXT Infra Trust": new URL(
    "../../../assets/invit-logos/nxt.png",
    import.meta.url
  ).href,
  "Orient Infra Trust": new URL(
    "../../../assets/invit-logos/orient.png",
    import.meta.url
  ).href,
  "Sustainable Energy Infra Trust": new URL(
    "../../../assets/invit-logos/sustainable.png",
    import.meta.url
  ).href,
  "Shrem Invit": new URL(
    "../../../assets/invit-logos/shrem.png",
    import.meta.url
  ).href,
  "Roadstar Infra Investment Trust": new URL(
    "../../../assets/invit-logos/roadstar.png",
    import.meta.url
  ).href,
  "National Highways Infra Trust": new URL(
    "../../../assets/invit-logos/national.png",
    import.meta.url
  ).href,
  "Digital Fibre Infrastructure Trust": new URL(
    "../../../assets/invit-logos/digital.png",
    import.meta.url
  ).href,
  "Intelligent Supply Chain Infrastructure Trust": new URL(
    "../../../assets/invit-logos/intelligent.png",
    import.meta.url
  ).href,
};

const reitcard1 = new URL("../../../assets/reitCard1.png", import.meta.url)
  .href;
const reitcard2 = new URL("../../../assets/reitCard2.png", import.meta.url)
  .href;
const compareArrow = new URL(
  "../../../assets/compare-arrow.png",
  import.meta.url
).href;

export default function InvitsListing() {
  const navigate = useNavigate();
  const { activeToggle, setActiveToggle, setCurrentInvit } = useParameters();
  const [loading, setLoading] = useState(true);
  const [filteredInvits, setFilteredInvits] = useState<any[]>([]);
  const { parameters, setParameters } = useParameters();
  const { isAuthenticated } = useAuth();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const cardsRef = React.useRef<HTMLDivElement | null>(null);

  const handleProtectedClick = () => {
    if (!isAuthenticated) {
      toast.error("You need to login first!");
      return false;
    }
    return true;
  };

  const handleFinancialClick = () => navigate("/invits/financial-compare");
  const handleOperationalClick = () => navigate("/invits/operational-compare");

  useEffect(() => {
    const loadInvits = async () => {
      try {
        setLoading(true);

        const isPublic = activeToggle === "Public";
        const data = await service.getInvits(isPublic);

        // Listing date mapping (same as InvitBanner)
        const listingDates: Record<string, string> = {
          "IRB InvIT Fund": "18 May 2017",
          "Indus Infra Trust": "12 March 2024",
          "PowerGrid InvIT": "14 May 2021",
          "IndiGrid InvIT": "6 June 2017",
          "Capital Infra Trust": "17 January 2025",
        };

        const processed = data.map((invit: any, idx: number) => {
          const latestYear =
            invit.financialMetrics?.[invit.financialMetrics.length - 1];
          const latestQuarter = getLatestValidQuarter(latestYear);

          const mcValue = findLatestValidValue(
            invit.financialMetrics,
            "marketCapitalisation"
          );

          const aumValue = findLatestValidValue(
            invit.financialMetrics,
            "assetsUnderManagementOrEnterpriseValue"
          );

          const marketCapitalisation = toWholeNumber(mcValue);
          const assetsUnderManagement = toWholeNumber(aumValue);

          const latestOperationalYear =
            invit.operationalMetrics?.[invit.operationalMetrics.length - 1];
          const latestSemiAnnual =
            latestOperationalYear?.semiAnnuals?.[
              latestOperationalYear.semiAnnuals.length - 1
            ];

          let sectorOfOperations =
            latestSemiAnnual?.sectorOfOperation?.name ?? "-";

          const industryOverrides: Record<string, string> = {
            "Altius Telecom Infrastructure Trust": "Telecommunication",
            "Digital Fibre Infrastructure Trust": "Telecommunication",
            "Energy Infrastructure Trust": "Oil & Gas",
            "Sustainable Energy Infra Trust": "Renewable",
            "Maple Infrastructure Trust": "Roads",
            "Anzen India Energy Yield Plus Trust": "Energy",
            "Intelligent Supply Chain Infrastructure Trust": "Warehousing"
          };

          if (industryOverrides[invit.name]) {
            sectorOfOperations = industryOverrides[invit.name];
          }

          const allProjects: string[] =
            invit.operationalMetrics
              ?.flatMap(
                (year: any) =>
                  year.semiAnnuals?.map(
                    (sa: any) => sa.sectorOfOperation?.nameOfProject?.value
                  ) || []
              )
              .filter(Boolean) || [];

          const uniqueProjects = new Set(allProjects);

          const industryImage = invitLogos[invit.name];

          return {
            ...invit,
            image: industryImage,
            listingDate: listingDates[invit.name] || "N/A",
            sectorOfOperations,
            totalUniqueProjects: uniqueProjects.size,
            marketCapitalisation,
            assetsUnderManagementOrEnterpriseValue: assetsUnderManagement,
          };
        });

        setParameters(processed);
      } catch (err) {
        console.error("Error loading InvITs:", err);
      } finally {
        setLoading(false);
      }
    };

    loadInvits(); // ✅ ALWAYS reload based on activeToggle
  }, [activeToggle]);

  useEffect(() => {
    let list: any[] = [];

    if (activeToggle === "Public") {
      list = parameters.filter(
        (invit: any) =>
          invit.isPublic === true || invit.nature?.toLowerCase() === "public"
      );
    } else {
      list = parameters.filter(
        (invit: any) =>
          invit.isPublic === false || invit.nature?.toLowerCase() === "private"
      );
    }

    list.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredInvits(list);
  }, [activeToggle, parameters]);

  if (loading) {
    return (
      <div className="invits-page">
        <LoadingOverlay
          message="Loading InvITs"
          overlay={false}
          isVisible={true}
        />
      </div>
    );
  }

  // Pagination logic (only for Private)
  const isPrivate = activeToggle === "Private";
  const totalPages = isPrivate
    ? Math.ceil(filteredInvits.length / itemsPerPage)
    : 1;

  const paginatedInvits = isPrivate
    ? filteredInvits.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      )
    : filteredInvits;

  return (
    <div className="invits-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-text">
          <h1>
            Discover, Compare, and Value InvITs{" "}
            <span>— for Smarter Investing.</span>
          </h1>
          <p>
            Use our intuitive comparison tool to analyze assets, returns, and
            operational performance — and pick the one that fits your financial
            goals.
          </p>
        </div>
      </section>

      {/* Header Section */}
      <div className="reits-header">
        <div className="reits-header__top">
          <h3>
            Quickly Compare InvITs and <br />
            <span>Unlock Key Insights</span>
          </h3>
        </div>

        <div className="reits-header__bottom">
          <div className="reits-toggle">
            <button
              className={`toggle-btn ${
                activeToggle === "Public" ? "active" : ""
              }`}
              onClick={() => setActiveToggle("Public")}
            >
              Public
            </button>
            <button
              className={`toggle-btn ${
                activeToggle === "Private" ? "active" : ""
              }`}
              onClick={() => setActiveToggle("Private")}
            >
              Private
            </button>
          </div>
        </div>
      </div>

      {/* InvIT Cards */}
      <div className="reit-cards" ref={cardsRef}>
        {paginatedInvits.length > 0 ? (
          paginatedInvits.map((invit, idx) => (
            <div key={idx} className="reit-card">
              <img src={invit.image} alt={invit.name} className="reit-banner" />
              <div className="reit-content">
                <div className="reit-header">
                  <div className="reit-title-block">
                    <h3>{invit.name}</h3>
                  </div>
                  <div className="reit-meta">
                    {activeToggle === "Public" && (
                      <p className="listing-date">
                        Listing on : {invit.listingDate}
                      </p>
                    )}
                    <button
                      className="compare-btn"
                      onClick={() => {
                        if (handleProtectedClick()) {
                          handleOperationalClick();
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <span className="compare-btn-text">Compare</span>
                      <span className="compare-btn-icon">
                        <img src={compareArrow} alt="compareArrow" />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="reit-stats-row">
                  <div className="stat-block">
                    <span>Industry</span>
                    <p>{invit.sectorOfOperations}</p>
                  </div>

                  <div className="stat-block">
                    <span>Projects</span>
                    <p>
                      {invit.totalUniqueProjects
                        ? invit.totalUniqueProjects
                        : "-"}
                    </p>
                  </div>

                  <div className="stat-block">
                    <span>Market Capitalization (₹ Mn.)</span>
                    <p>{invit.marketCapitalisation}</p>
                  </div>

                  <div className="stat-block">
                    <span>Assets Under Management (₹ Mn.)</span>
                    <p>{invit.assetsUnderManagementOrEnterpriseValue}</p>
                  </div>

                  {/* Buttons inside same row */}
                  <div className="reit-actions">
                    <NavLink
                      to={isAuthenticated ? `/invits/${invit.id}` : "#"}
                      onClick={(e) => {
                        if (!isAuthenticated) {
                          e.preventDefault();
                          toast.error("You need to login first!");
                        } else {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <button className="btn-secondary">Know More</button>
                    </NavLink>

                    <NavLink
                      to={
                        isAuthenticated ? `/invits/${invit.id}/valuation` : "#"
                      }
                      state={{ invitId: invit.id }}
                      onClick={(e) => {
                        if (!isAuthenticated) {
                          e.preventDefault();
                          toast.error("You need to login first!");
                        } else {
                          setCurrentInvit(invit);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                    >
                      <button className="btn-primary">Valuation</button>
                    </NavLink>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data-text">
            Currently no {activeToggle} listings available.
          </div>
        )}

        {isPrivate && totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage((prev) => {
                  const nextPage = prev - 1;
                  setTimeout(() => {
                    if (cardsRef.current) {
                      const y =
                        cardsRef.current.getBoundingClientRect().top +
                        window.scrollY -
                        100;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }, 0);
                  return nextPage;
                });
              }}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={currentPage === idx + 1 ? "active" : ""}
                onClick={() => {
                  setCurrentPage(idx + 1);
                  setTimeout(() => {
                    if (cardsRef.current) {
                      const y =
                        cardsRef.current.getBoundingClientRect().top +
                        window.scrollY -
                        100;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }, 0);
                }}
              >
                {idx + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage((prev) => {
                  const nextPage = prev + 1;
                  setTimeout(() => {
                    if (cardsRef.current) {
                      const y =
                        cardsRef.current.getBoundingClientRect().top +
                        window.scrollY -
                        100;
                      window.scrollTo({ top: y, behavior: "smooth" });
                    }
                  }, 0);
                  return nextPage;
                });
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Compare Section */}
      <div className="compare-section">
        <div className="card">
          <img
            src={reitcard1}
            alt="Compare InvIT Properties"
            className="card-img"
          />
          <h3 className="card-title">Compare InvITs Financial Metrics</h3>
          <p className="card-desc">
            Dive deep into financial ratios, market cap, and performance across
            InvITs.
          </p>
          <button
            className="card-btn"
            onClick={() => {
              if (handleProtectedClick()) {
                handleFinancialClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>

        <div className="card">
          <img src={reitcard2} alt="Compare InvITs" className="card-img" />
          <h3 className="card-title">Compare InvITs Operational Metrics</h3>
          <p className="card-desc">
            Analyze project performance, operations, and unique project counts.
          </p>
          <button
            className="card-btn"
            onClick={() => {
              if (handleProtectedClick()) {
                handleOperationalClick();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
